import React, { useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { useCarousel } from './CarouselContext';
import { getTransitionDuration } from './utils';

import * as styles from './Carousel.module.css';

import type { MediaConfig } from './types';

type CarouselPaginationProps = JSX.IntrinsicElements['div'] & {
  PageIndicatorComponent?: React.FC<Partial<{ index: number; currentIndex: number } & MediaConfig>>;
};

export const CarouselPagination: React.FC<CarouselPaginationProps> = ({ PageIndicatorComponent, ...props }) => {
  const {
    carouselState: { currentIndex, currentConfig },
    slideCount,
  } = useCarousel();

  const { slidesToShow, autoplay, infinite } = currentConfig;
  const pageCount = infinite ? slideCount : slideCount - (slidesToShow - 1);

  const indicators = useMemo(
    () => Array.from({ length: pageCount }, (_, i) => i),
    [pageCount, slidesToShow, currentIndex],
  );

  return slidesToShow < slideCount ? (
    <div {...props} className={clsx(styles.pagination, props.className)}>
      {indicators.map((_, index) =>
        PageIndicatorComponent ? (
          <PageIndicatorComponent key={index} index={index} currentIndex={currentIndex} {...currentConfig} />
        ) : (
          <CarouselPageIndicator key={index} activateOnIndex={index} progressBarMode={autoplay} />
        ),
      )}
    </div>
  ) : null;
};

type CarouselPageIndicatorProps = JSX.IntrinsicElements['div'] & {
  activateOnIndex?: 'all' | number;
  progressBarMode?: boolean;
};

export const CarouselPageIndicator: React.FC<CarouselPageIndicatorProps> = ({
  activateOnIndex,
  progressBarMode,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const {
    internalState,
    carouselState: { currentIndex, currentConfig },
  } = useCarousel();

  const { autoplay, autoplayInterval } = currentConfig;

  useEffect(() => {
    if (!ref.current) return;

    if (autoplay && progressBarMode) {
      ref.current.classList.remove(styles.active);

      if (activateOnIndex === 'all') {
        ref.current.classList.remove(styles.animate);
        void ref.current.offsetHeight; // Triggers reflow to apply style change immediately
        ref.current.classList.add(styles.animate);
      } else if (typeof activateOnIndex === 'number') {
        currentIndex === activateOnIndex
          ? ref.current.classList.add(styles.animate)
          : ref.current.classList.remove(styles.animate);
      } else {
        ref.current.classList.remove(styles.animate);
      }
      return;
    }

    ref.current.classList.remove(styles.animate);

    if (activateOnIndex === 'all') {
      ref.current.classList.add(styles.active);
    } else if (typeof activateOnIndex === 'number') {
      currentIndex === activateOnIndex
        ? ref.current.classList.add(styles.active)
        : ref.current.classList.remove(styles.active);
    } else {
      ref.current.classList.remove(styles.active);
    }
  }, [currentIndex, activateOnIndex, progressBarMode, autoplay]);

  return (
    <div
      ref={ref}
      {...props}
      className={clsx(styles.pageIndicator, progressBarMode && styles.progressBar, props.className)}
      style={{
        animationDuration: `${autoplayInterval}ms`,
        transitionDuration: `${getTransitionDuration(internalState.current.navigationMode, currentConfig)}ms`,
        ...props.style,
      }}
    />
  );
};
